import { Card, Stack, Heading, Button, vars } from '@etg/wings';
import { css } from '@eti/styles';
import { useTranslation, useDateFormat, useSearch } from '@eti/providers';
import { price as priceUtils } from '@eti/utils';
import { useHistory } from 'react-router-dom';
import type { BestPricePerPeriod } from '@eti/schema-types';
import { TripType } from '@eti/schema-types';
import { routes } from '../../constants/routesAndApisConstants';
import { ONE_WAY } from '../../constants/tripTypesConstants';
import { getDateFromDateString } from './utils';
import type { Location } from './models';

const searchDealButtonStyles = css`
  width: 100%;
`;
const startPriceStyles = css`
  color: #737373;
  font-weight: 700;
`;
const flightTypeStyles = css`
  color: ${vars.colors.signal.information.foreground};
  font-weight: 700;
`;
const flightCardStyles = css`
  border: 1px solid ${vars.colors.divider};
  height: 100%;
`;
const departureTextStyles = css`
  font-size: 0.75rem;
  text-align: center;
`;

export interface PriceListCardProps {
  destination: Location;
  flight: BestPricePerPeriod;
  origin: Location;
  tripType: string;
}

const PriceCard = ({ tripType, flight, destination, origin }: PriceListCardProps) => {
  const { t } = useTranslation();
  const { shortWeekday: dateFormat } = useDateFormat();
  const { setSearchData } = useSearch();
  const history = useHistory();

  const handleSearchClick = () => {
    if (origin.name && destination.name) {
      const searchData = {
        travelers: { adults: 1, childAges: [] },
        routes: flight.bounds.map((bound) => ({
          originCode: bound.origin,
          destinationCode: bound.destination,
          departureDate: bound.date,
        })),
        date: {
          departure: flight.bounds[0].date,
          return: tripType !== ONE_WAY ? flight.bounds[flight.bounds.length - 1].date : '',
        },
        origin: { city: origin.name },
        destination: { city: destination.name },
        tripType: tripType === ONE_WAY ? TripType.OneWay : TripType.Return,
      };
      if (searchData && setSearchData) {
        setSearchData(searchData);
      }
      history.push(routes.RESULT);
    }
  };

  return (
    <Card className={flightCardStyles} data-testid="price-list" padding={16}>
      <Stack align="center" spacing={16}>
        <Stack align="center" spacing={8}>
          <Heading data-testid="price-list-within" level={3}>
            {t('SemComponent.PriceLists.CardHeadLine.Text', flight.period)}
          </Heading>
          <p
            className={flightTypeStyles}
            data-testid={tripType === ONE_WAY ? 'price-list-oneWay' : 'price-list-return'}
          >
            {tripType === ONE_WAY
              ? t('SemComponent.PriceLists.OneWay')
              : t('SemComponent.PriceLists.Return')}
          </p>
          <span className={startPriceStyles}>{t('SemComponent.PriceLists.StartPrice.Text')}</span>
          <Heading data-testid="price-list-price" level={3}>
            {priceUtils.formatPrice(flight.price)}
          </Heading>
        </Stack>
        <Button
          className={searchDealButtonStyles}
          data-testid="price-list-searchDeal"
          onClick={handleSearchClick}
          variant="primary"
        >
          {t('SemComponent.PriceLists.Button.Text')}
        </Button>
        <p className={departureTextStyles} data-testid="price-list-date">
          {t(
            'SemComponent.PriceLists.DepartureDate.Text',
            getDateFromDateString(flight.bounds[0].date, dateFormat),
          )}
        </p>
      </Stack>
    </Card>
  );
};

export default PriceCard;
